import {patientApiRequest} from "../lib/api";

// A patient's own forms, read through the server's operations (forms.ownForms,
// forms.ownForm) instead of the generic CRUD passthrough. The list carries no
// answers and no sections; opening one fetches it in full, so what the server
// adds to an open form (the promotions block) reaches the signed-in portal.
// Submit stays on /submit-form.

const OWN = "/patient-app/own-forms";
const dependentBase = (dependentPatientId) =>
  `/patient-app/dependents/${encodeURIComponent(dependentPatientId)}/own-forms`;

const PAGE_SIZE = 100;
const MAX_PAGES = 50;

// The list is paged by the server (limit at most 100, then `nextCursor`, null on
// the last page). The screen groups and counts every form, so it reads them all:
// a stop on a repeated cursor or after MAX_PAGES keeps a misbehaving answer from
// looping. `recordScope` is the first page's.
async function fetchAllForms(auth, url) {
  const forms = [];
  const seen = new Set();
  let first = null;
  let cursor = null;
  for (let page = 0; page < MAX_PAGES; page += 1) {
    const answer = await patientApiRequest(auth, {
      method: "GET",
      url,
      params: cursor ? {limit: PAGE_SIZE, cursor} : {limit: PAGE_SIZE},
    });
    if (!first) first = answer || {};
    if (Array.isArray(answer?.forms)) forms.push(...answer.forms);
    cursor = typeof answer?.nextCursor === "string" && answer.nextCursor ? answer.nextCursor : null;
    if (!cursor || seen.has(cursor)) break;
    seen.add(cursor);
  }
  return {...first, forms, nextCursor: null};
}

/** @returns {Promise<{forms: object[]}>} every form, all pages */
export function fetchOwnForms(auth) {
  return fetchAllForms(auth, OWN);
}

/** @returns {Promise<{formSubmission: object, patient: object|null}>} */
export function fetchOwnForm(auth, formSubmissionId) {
  return patientApiRequest(auth, {
    method: "GET",
    url: `${OWN}/${encodeURIComponent(formSubmissionId)}`,
  });
}

export function fetchDependentOwnForms(auth, dependentPatientId) {
  return fetchAllForms(auth, dependentBase(dependentPatientId));
}

export function fetchDependentOwnForm(auth, dependentPatientId, formSubmissionId) {
  return patientApiRequest(auth, {
    method: "GET",
    url: `${dependentBase(dependentPatientId)}/${encodeURIComponent(formSubmissionId)}`,
  });
}

/**
 * The answer of forms.ownForm in the shape the editor (CompleteForm, then
 * TempForm) reads a submission in: `fsid`, `formTitle`, `pid`, `sections`,
 * `branding`, `draftSavedAt`. Only those fields: TempForm sends this object
 * back on submit, so a field it does not know would be written to the record.
 */
export function editorFormOf(answer) {
  const form = answer?.formSubmission || {};
  return {
    fsid: form.formSubmissionId,
    aid: form.appointmentId || undefined,
    formTitle: form.title || undefined,
    pid: form.pid,
    status: form.status,
    completed: form.completed === true,
    sections: Array.isArray(form.sections) ? form.sections : [],
    branding: form.branding || undefined,
    draftSavedAt: form.draftSavedAt || undefined,
    lastModified: form.lastModified || undefined,
  };
}
